import { Input, InputGroup, InputLeftAddon, InputProps } from '@chakra-ui/react'
import { ChangeEvent, forwardRef, useEffect, useState } from 'react'

import { formatCurrencyValue } from '../../helpers'

/** R$ 9.999.999.999,99. Acima disso a fita de dígitos só acumula sem caber na tela. */
const MAX_DIGITS = 12

/** `1234.5` -> `'123450'`. A fita de dígitos é o que o usuário digitou, em centavos. */
export const toMoneyDigits = (value: number | null): string => (value == null ? '' : String(Math.round(value * 100)))

/** `'123450'` -> `1234.5`. Campo vazio é `null`, não `0` — quem decide se vazio vale zero é o formulário. */
export const fromMoneyDigits = (digits: string): number | null => (digits === '' ? null : Number(digits) / 100)

export interface IMoneyInputProps extends Omit<InputProps, 'onChange' | 'value'> {
  value: number | null
  onChange: (value: number | null) => void
}

/**
 * Campo de dinheiro. **O valor que entra e sai é o número**, não a string formatada.
 *
 * ```tsx
 * <FormField label="Preço" error={formErrors.price} isRequired>
 *   <MoneyInput value={form.price} onChange={(price) => setForm({ ...form, price: price ?? 0 })} />
 * </FormField>
 * ```
 *
 * Eram 9 implementações com **duas semânticas incompatíveis** em campos de aparência idêntica:
 * centavos acumulados (digitar `5` dá `R$ 0,05`) em ~14 campos e digitação livre com vírgula
 * (digitar `5` dá `R$ 5,00`) em 13. A semântica agora é uma — **centavos acumulados**, que era a
 * maioria e é a única que não tem estado ambíguo: não existe "o usuário já digitou a vírgula?".
 *
 * E o `R$` é o `InputLeftAddon`, sempre. Era renderizado de quatro jeitos — addon, embutido no
 * valor (`value={`R$ ${formatted}`}`), no texto do rótulo (`Valor de venda (R$)`) e ausente. O
 * `EventFormTabs/TicketsTab` fazia os dois primeiros **no mesmo arquivo**, em dois campos de preço
 * a poucas linhas um do outro.
 *
 * Sai também o estado duplicado que a string formatada obrigava: `registration_fee` +
 * `formattedValue`, `price` + `formattedPrice`. O componente guarda a fita de dígitos; a página
 * guarda o número.
 */
export const MoneyInput = forwardRef<HTMLInputElement, IMoneyInputProps>(function MoneyInput(
  { value, onChange, size, isDisabled, ...rest },
  ref
) {
  const [digits, setDigits] = useState(() => toMoneyDigits(value))

  // Só ressincroniza quando o número que chega é outro — carregar o formulário, resetar, receber
  // a extração do comprovante. Sem essa comparação a digitação briga com o próprio pai, que acabou
  // de receber o valor emitido daqui.
  useEffect(() => {
    setDigits((current) => (fromMoneyDigits(current) === value ? current : toMoneyDigits(value)))
  }, [value])

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    // Os zeros à esquerda caem **todos**, e não só os que precedem outro dígito: apagar o último
    // dígito de `0,05` deixa a fita em `'00'`, e mantendo um zero o campo trava em `R$ 0,00` —
    // cada backspace seguinte reproduz a mesma fita e o valor nunca chega a `null`. Os campos
    // opcionais (valor da moldura, parcela mínima, taxa de matrícula) ficavam sem como esvaziar.
    const next = event.target.value.replace(/\D/g, '').replace(/^0+/, '').slice(0, MAX_DIGITS)

    setDigits(next)
    onChange(fromMoneyDigits(next))
  }

  const numericValue = fromMoneyDigits(digits)

  return (
    <InputGroup size={size}>
      {/* O addon não herda o estado do campo: sem isto o `R$` fica aceso ao lado de um campo
          apagado, que é o que acontece nos preços de curso quando a modalidade está desligada. */}
      <InputLeftAddon opacity={isDisabled ? 0.4 : undefined}>R$</InputLeftAddon>
      <Input
        ref={ref}
        inputMode="numeric"
        placeholder="0,00"
        isDisabled={isDisabled}
        {...rest}
        value={numericValue == null ? '' : formatCurrencyValue(numericValue)}
        onChange={handleChange}
      />
    </InputGroup>
  )
})
